// src/pages/ProposalEdit.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWeb3 } from '../contexts/Web3Context';
import { useNotification } from '../contexts/NotificationContext';
import axios from 'axios';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import TokenSelectList from '../components/TokenSelectList';
import LoadingSpinner from '../components/LoadingSpinner';
import { XMarkIcon } from '@heroicons/react/24/outline';

const ProposalEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { account, isOasysNetwork, connectWallet, switchToOasysNetwork, signMessage } = useWeb3();
  const { showNotification } = useNotification();

  const [proposal, setProposal] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedToken, setSelectedToken] = useState(null);
  const [votingMethod, setVotingMethod] = useState('ONE_ADDRESS_ONE_VOTE');
  const [includeUnclaimed, setIncludeUnclaimed] = useState(false);
  const [startTime, setStartTime] = useState(new Date());
  const [endTime, setEndTime] = useState(new Date());
  const [choices, setChoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [error, setError] = useState(null);

  // 提案データの取得
  useEffect(() => {
    const fetchProposal = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/proposals/${id}`);
        const proposalData = response.data;

        setProposal(proposalData);
        setTitle(proposalData.title);
        setDescription(proposalData.description);
        setVotingMethod(proposalData.votingMethod);
        setIncludeUnclaimed(proposalData.includeUnclaimed);
        setStartTime(new Date(proposalData.startTime));
        setEndTime(new Date(proposalData.endTime));

        // 選択肢の設定
        if (proposalData.choices && proposalData.choices.length) {
          setChoices(proposalData.choices.map(choice => choice.text));
        }

        // トークン情報を取得
        const tokensResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/tokens`);
        // grantTokenがNULLでないトークンのみをフィルタリング
        const validTokens = tokensResponse.data.filter(token => token.grantToken);
        setTokens(validTokens);

        // 選択されているトークンを設定
        if (proposalData.token) {
          const token = validTokens.find(t => t.grantToken === proposalData.token.grantToken);
          setSelectedToken(token);
        }

        setIsLoading(false);
      } catch (error) {
        console.error('提案取得エラー:', error);
        setError('提案の取得に失敗しました');
        setIsLoading(false);
      }
    };

    if (id) {
      fetchProposal();
    }
  }, [id]);

  // 編集権限チェック
  useEffect(() => {
    const checkEditPermission = () => {
      if (!proposal || !account) return;

      // 作成者以外は編集不可
      if (proposal.creatorAddress.toLowerCase() !== account.toLowerCase()) {
        showNotification('この提案を編集する権限がありません', 'error');
        navigate(`/proposals/${id}`);
        return;
      }

      // 投票開始後は編集不可
      const now = new Date();
      const startTime = new Date(proposal.startTime);
      if (now >= startTime) {
        showNotification('投票開始後の提案は編集できません', 'error');
        navigate(`/proposals/${id}`);
        return;
      }
    };

    checkEditPermission();
  }, [proposal, account, id, navigate, showNotification]);

  // 選択肢を追加
  const addChoice = () => {
    setChoices([...choices, '']);
  };

  // 選択肢を削除
  const removeChoice = index => {
    if (choices.length <= 2) {
      showNotification('最低2つの選択肢が必要です', 'error');
      return;
    }
    const newChoices = [...choices];
    newChoices.splice(index, 1);
    setChoices(newChoices);
  };

  // 選択肢を更新
  const updateChoice = (index, value) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
  };

  // フォーム送信
  const handleSubmit = async e => {
    e.preventDefault();

    // バリデーション
    if (!account) {
      showNotification('ウォレットを接続してください', 'error');
      return;
    }

    if (!isOasysNetwork) {
      showNotification('OASYSネットワークに切り替えてください', 'error');
      return;
    }

    if (!title.trim()) {
      showNotification('タイトルを入力してください', 'error');
      return;
    }

    if (!description.trim()) {
      showNotification('提案内容を入力してください', 'error');
      return;
    }

    if (!selectedToken) {
      showNotification('トークンを選択してください', 'error');
      return;
    }

    // 選択肢のバリデーション
    const validChoices = choices.filter(choice => choice.trim() !== '');
    if (validChoices.length < 2) {
      showNotification('最低2つの有効な選択肢が必要です', 'error');
      return;
    }

    // 日時のバリデーション
    const now = new Date();
    if (startTime <= now) {
      showNotification('開始時間は現在より後に設定してください', 'error');
      return;
    }

    if (endTime <= startTime) {
      showNotification('終了時間は開始時間より後に設定してください', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      // 署名メッセージの作成
      const message = `提案を編集します\n提案ID: ${id}\nタイトル: ${title}\n${Date.now()}`;

      // 署名
      const signature = await signMessage(message);

      // 提案データの準備
      const proposalData = {
        title,
        description,
        tokenAddress: selectedToken.grantToken,
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        choices: validChoices,
        votingMethod,
        includeUnclaimed,
        signature,
        message,
      };

      // APIリクエスト
      await axios.put(`${process.env.REACT_APP_API_URL}/api/proposals/${id}`, proposalData);

      showNotification('提案が更新されました', 'success');
      navigate(`/proposals/${id}`);
    } catch (error) {
      console.error('提案更新エラー:', error);
      showNotification(error.response?.data?.error || '提案の更新に失敗しました', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ウォレット接続ハンドラー
  const handleConnect = async () => {
    if (!account) {
      await connectWallet();
    } else if (!isOasysNetwork) {
      await switchToOasysNetwork();
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 max-w-2xl mx-auto">
        <div className="text-center py-8">
          <div className="text-red-500 dark:text-red-400 text-xl font-bold mb-2">{error}</div>
          <button
            onClick={() => navigate(`/proposals/${id}`)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            提案詳細に戻る
          </button>
        </div>
      </div>
    );
  }

  // 接続されていない場合
  if (!account) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">提案を編集</h1>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          提案を編集するには、まずウォレットを接続してください。
        </p>
        <button
          onClick={handleConnect}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          ウォレット接続
        </button>
      </div>
    );
  }

  // OASYSネットワークに接続されていない場合
  if (!isOasysNetwork) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">提案を編集</h1>
        <p className="mb-6 text-gray-700 dark:text-gray-300">
          提案を編集するには、OASYSネットワークに切り替えてください。
        </p>
        <button
          onClick={switchToOasysNetwork}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          OASYSネットワークに切り替え
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-white">
        提案を編集
      </h1>

      <form onSubmit={handleSubmit}>
        {/* タイトル */}
        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            タイトル <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="提案のタイトル"
            required
          />
        </div>

        {/* 説明 → 提案内容に変更 */}
        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            提案内容 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            placeholder="提案の詳細な内容"
            rows="5"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            投票権コイン <span className="text-red-500">*</span>
          </label>

          {/* 選択されたコインを表示 */}
          {selectedToken && (
            <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
              <div className="flex items-center">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-300 font-bold mr-3">
                  <img
                    src={selectedToken.img}
                    alt={selectedToken.name}
                    className="w-12 h-12 rounded-full object-cover mr-3"
                    onError={e => {
                      // 画像が読み込めない場合はフォールバック
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-blue-800 dark:text-blue-300">
                    {selectedToken.name}
                  </div>
                  <div className="text-sm text-blue-600 dark:text-blue-400 flex items-center">
                    <span>{selectedToken.symbol}</span>
                    {selectedToken.pairType && (
                      <span className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-800 rounded-full text-xs">
                        {selectedToken.pairType}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedToken(null)}
                  className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          <TokenSelectList
            tokens={tokens}
            // selectedToken={selectedToken}
            onSelectToken={setSelectedToken}
          />
        </div>

        {/* 投票期間 */}
        <div className="mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              開始日時 <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={startTime}
              onChange={setStartTime}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="yyyy/MM/dd HH:mm"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              minDate={new Date()}
            />
          </div>

          <div>
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
              終了日時 <span className="text-red-500">*</span>
            </label>
            <DatePicker
              selected={endTime}
              onChange={setEndTime}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="yyyy/MM/dd HH:mm"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              minDate={startTime}
            />
          </div>
        </div>

        {/* 投票方式 */}
        <div className="mb-4">
          <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
            投票方式
          </label>
          <div className="flex gap-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                value="ONE_ADDRESS_ONE_VOTE"
                checked={votingMethod === 'ONE_ADDRESS_ONE_VOTE'}
                onChange={() => setVotingMethod('ONE_ADDRESS_ONE_VOTE')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700 dark:text-gray-300">1アドレス1票</span>
            </label>

            <label className="inline-flex items-center">
              <input
                type="radio"
                value="TOKEN_WEIGHTED"
                checked={votingMethod === 'TOKEN_WEIGHTED'}
                onChange={() => setVotingMethod('TOKEN_WEIGHTED')}
                className="form-radio h-4 w-4 text-blue-600"
              />
              <span className="ml-2 text-gray-700 dark:text-gray-300">コイン量比例</span>
            </label>
          </div>
        </div>

        {/* 未Claim対応 */}
        <div className="mb-6">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={includeUnclaimed}
              onChange={e => setIncludeUnclaimed(e.target.checked)}
              className="form-checkbox h-4 w-4 text-blue-600"
            />
            <span className="ml-2 text-gray-700 dark:text-gray-300">
              未Claimのコインも投票権に含める
            </span>
          </label>
        </div>

        {/* 選択肢 - "選択肢を追加"ボタンを横に配置 */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold">
              選択肢 <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={addChoice}
              className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              <span>選択肢を追加</span>
            </button>
          </div>

          {choices.map((choice, index) => (
            <div key={index} className="flex items-center mb-2">
              <input
                type="text"
                value={choice}
                onChange={e => updateChoice(index, e.target.value)}
                className="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                placeholder={`選択肢 ${index + 1}`}
                required
              />

              <button
                type="button"
                onClick={() => removeChoice(index)}
                className="ml-2 p-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                disabled={choices.length <= 2}
              >
                <MinusIcon className="h-5 w-5" />
              </button>
            </div>
          ))}
        </div>

        {/* 送信ボタン */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => navigate(`/proposals/${id}`)}
            className="px-4 py-2 mr-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            disabled={isSubmitting}
          >
            キャンセル
          </button>

          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition disabled:bg-blue-300 dark:disabled:bg-blue-800"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2">保存中...</span>
              </span>
            ) : (
              '変更を保存'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProposalEdit;
